import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useQuery } from "react-query";
import { debounce } from "lodash";
import { useTranslation } from 'react-i18next';
import { useSnackbarContext } from "components/snackbar";

// Components
import Pagination from "./components/Pagination";
import Header from "./components/Header";
import ProgressBar from "./components/ProgressBar";
import Customers from "./components/Customers";
import Spinner from "components/spinner";

// Types
import { IAccordionStates } from "./types";

function DuplicateCustomers() {
  const { t } = useTranslation();
  const tBase = "views.duplicates.customers.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const { showSnackbar } = useSnackbarContext();
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false); // used for scan / rescan
  const [initialLoad, setInitialLoad] = useState(true); // used for scan / rescan
  const [customerData, setCustomerData] = useState([] as any[]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]); // For backward navigation
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const savedItemsPerPage = localStorage.getItem("itemsPerPage");
    return savedItemsPerPage ? JSON.parse(savedItemsPerPage) : 20;
  });
  const [paginationInfo, setPaginationInfo] = useState({
    hasMore: false,
    hasPrev: false,
    totalCount: 0,
    firstCursor: null as string | null,
    lastCursor: null as string | null,
  });
  const [accordionStates, setAccordionStates] = useState<IAccordionStates>({});
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [showSpinnerMessage, setShowSpinnerMessage] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState("");
  const [clickedArea, setClickedArea] = useState(""); // used to determine which area was clicked (scan or rescan)
  const [scanComplete, setScanComplete] = useState(false); // used to determine if scan is complete
  const [yesScanned, setYesScanned] = useState(false); // used to determine if scan has been done before
  const [duplicatesExist, setDuplicatesExist] = useState(false);
  const [lastScanned, setLastScanned] = useState<Date | null>(null);
  // searchInput drives the text field; searchTerm is the debounced value
  // actually sent to the backend. Keeping them separate lets the user type
  // freely without firing a request per keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const initialDataLoadDone = useRef(false);

  // Ensure immediate state changes are reflected before the browser paints
  useLayoutEffect(() => {
    setLoading(true);
    checkIfScanned();
    checkIfExists();
    setLoading(false);
  }, []);

  // Accordion
  const toggleAccordion = (id: string) => {
    setAccordionStates(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  const updateItemsPerPage = (newItemsPerPage: number) => {
    localStorage.setItem("itemsPerPage", JSON.stringify(newItemsPerPage));
    setItemsPerPage(newItemsPerPage);
    // Reset to first page when changing items per page
    setCursor(null);
    setCursorHistory([]);
    getCustomerData('next', null);
  };

  const getLastScanned = async () => {
    const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/lastScanned`);
    if (!res.data) return;
    const date = new Date(res.data);
    setLastScanned(date);
  };

  // Track the previous inProgress value so we can detect the
  // true → false transition and trigger the post-scan refresh exactly
  // once (the scanComplete flag hands off to the list/status refetch
  // effect below).
  const wasInProgress = useRef(false);

  // Poll the backend's real scan state instead of depending on SSE. The
  // query keeps polling every 500ms while the server reports the scan
  // in progress and pauses otherwise. This is the v2 pattern ported to
  // legacy — survives reloads, honors concurrent tabs, and doesn't
  // depend on the browser holding a long-lived SSE connection through
  // CloudFront/ALB idle timeouts.
  //
  // Transition detection lives in onSuccess (not a useEffect on data).
  // React Query v3's structural sharing re-uses the previous data
  // reference when the new response is deep-equal to the old one, so a
  // scan that finishes in <500ms (small tenants or no-op rescans) would
  // return the same `{inProgress:false, progress:100, message:"Complete"}`
  // as the initial page load. The effect would never re-fire, the
  // transition would never be observed, and loading2 would stay on.
  // onSuccess runs after every successful fetch regardless of whether
  // the data reference changed.
  const scanStatusQuery = useQuery<{ inProgress: boolean; progress: number; message: string }>(
    "duplicates-scan-status",
    async () => {
      // Cache-bust every poll. CloudFront sits between the browser and
      // the API and can cache responses for up to its TTL regardless of
      // the origin's Cache-Control header (any cache entry created
      // before no-store shipped lives until TTL). A unique query param
      // each request guarantees a cache miss and fresh origin read.
      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/scan-status?t=${Date.now()}`
      );
      return res.data;
    },
    {
      refetchInterval: (data) => (data?.inProgress ? 500 : false),
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      retry: false,
      onSuccess: (status) => {
        if (!status) return;

        if (wasInProgress.current && !status.inProgress) {
          // Scan just completed — success or failure.
          if (typeof status.message === "string" && status.message.startsWith("Failed:")) {
            showSnackbar(status.message, "error");
          }
          setLoading2(false);
          setProgress(0);
          setProgressMessage("");
          setClickedArea("");
          setScanComplete(true);
        } else if (status.inProgress) {
          // Keep the progress bar in step with the backend. Bucket split
          // matches the legacy UI: DB 0-40 drives the fast "scanning"
          // 0-5% slice, DB 40-100 drives the 5-100% "saving" slice.
          if (status.progress < 40) {
            setProgress((status.progress / 40) * 100 * 0.05);
          } else {
            setProgress(5 + ((status.progress - 40) / 60) * 100 * 0.95);
          }
          setProgressMessage(status.message);
          // First detection of in-flight scan (e.g., resumed after reload) —
          // show the progress bar without requiring a button click.
          if (!wasInProgress.current) {
            setLoading2(true);
            setClickedArea((prev) => prev || "rescan");
          }
        }

        wasInProgress.current = status.inProgress;
      },
    }
  );

  // Used for scan / re-scan button — fire-and-forget the scan and let
  // the polling query drive the progress bar.
  const findDuplicates = async (whereClicked: string) => {
    setScanComplete(false);
    setProgress(0);
    setProgressMessage("");
    if (whereClicked === "rescan") {
      setClickedArea("rescan");
    } else if (whereClicked === "scan") {
      setClickedArea("scan");
    }
    setLoading2(true);

    // Prime the transition detector. The completion branch of the
    // scanStatusQuery effect only fires on `true → false`. On a small
    // tenant the scan can finish before the first poll lands, so if we
    // don't manually flip this to true here the first observation is
    // already `false` and the handler never runs — loading2 gets stuck
    // on and the list never refreshes.
    wasInProgress.current = true;

    try {
      await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/scan`
      );
      // Nudge the scan-status query immediately so the bar shows progress
      // without waiting for the next polling tick.
      scanStatusQuery.refetch();
    } catch (error: any) {
      // 409 = scan already running on the server; just let polling
      // surface the in-flight state. Anything else is a real failure.
      if (error?.response?.status !== 409) {
        console.error("Failed to start scan:", error);
        showSnackbar(tr("Failed to start scan") || "Failed to start scan", "error");
        setLoading2(false);
        setProgress(0);
        setProgressMessage("");
        setClickedArea("");
      }
    }
  };

  // Gets customer data using cursor-based pagination
  const getCustomerData = useCallback(async (direction: 'next' | 'prev' = 'next', newCursor: string | null = null) => {
    setLoading(true);
    try {
      // Use whatever cursor the caller passed — including null, which
      // means "start from the first page". The previous fallback to
      // state cursor hid the post-rescan case: a scan rebuilds
      // duplicate IDs so the old state cursor is no longer valid and
      // the query silently returned empty. Callers (paginateNext,
      // paginatePrev, scanComplete refetch) already pass the cursor
      // they intend, so dropping the fallback is semantically equivalent
      // for existing flows.
      const currentCursor = newCursor;
      const params: any = {
        limit: itemsPerPage,
        direction: direction
      };
      if (currentCursor) {
        params.cursor = currentCursor;
      }
      if (searchTerm) {
        params.search = searchTerm;
      }

      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/getCustomerDataPaginated`, {
        params
      });
      
      setCustomerData(res.data.customers);
      setPaginationInfo({
        hasMore: res.data.hasMore,
        hasPrev: res.data.hasPrev,
        totalCount: res.data.totalCount,
        firstCursor: res.data.firstCursor,
        lastCursor: res.data.lastCursor,
      });
      setDuplicateCount(res.data.totalCount);
      
      // Update cursor after navigation
      // Note: "next" = decrement (lower IDs), "prev" = increment (higher IDs)
      if (res.data.customers.length > 0) {
        if (direction === 'next') {
          // Moving to "next" (decrement): store current firstCursor in history for going back
          // Only store if we're not on the first page (cursor is not null)
          if (res.data.firstCursor && currentCursor !== null) {
            setCursorHistory(prev => [...prev, res.data.firstCursor]);
          }
          // Update cursor to lastCursor (lowest ID on page) for next decrement navigation
          setCursor(res.data.lastCursor);
        } else {
          // Moving to "prev" (increment): the API returned records with higher IDs
          // Remove last entry from history since we've navigated back to that page
          const newHistory = [...cursorHistory];
          if (newHistory.length > 0) {
            newHistory.pop();
            setCursorHistory(newHistory);
          }
          // Update cursor: if we still have history, use lastCursor
          // Otherwise, we're at the top (highest IDs), so cursor should be null
          if (newHistory.length === 0) {
            setCursor(null); // We're at the top
          } else {
            // Set cursor to lastCursor of the page we're now on
            setCursor(res.data.lastCursor || null);
          }
        }
      }
      
      setLoading(false);
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setLoading(false);
    }
    // Deps intentionally exclude cursor — we no longer read state
    // cursor inside. cursorHistory is read for the "prev" branch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursorHistory, itemsPerPage, searchTerm]);

  const checkIfScanned = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/HasBeenScanned`);
      setYesScanned(res.data);
    } catch (error) {
      console.error("Error checking if scanned: ", error);
    }
  };

  const checkIfExists = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/duplicates/customers/CheckIfDuplicatesExist`);
      setDuplicatesExist(res.data);
    } catch (error) {
      console.error("Error checking if scanned: ", error);
    }
  };
  
  // Load initial data once when duplicates are ready (avoid re-running when getCustomerData identity changes)
  useEffect(() => {
    if (yesScanned && duplicatesExist && !initialDataLoadDone.current) {
      initialDataLoadDone.current = true;
      getCustomerData('next', null);
    }
  }, [yesScanned, duplicatesExist, getCustomerData]);

  // Debounce the search box so each keystroke doesn't hit the server.
  // The trailing 300ms window is a good balance of responsiveness vs
  // load — a fast typist sending "acme" fires one request on the final
  // keystroke, not four.
  const debouncedSetSearch = useRef(
    debounce((value: string) => setSearchTerm(value.trim()), 300)
  ).current;

  useEffect(() => {
    debouncedSetSearch(searchInput);
    return () => debouncedSetSearch.cancel();
  }, [searchInput, debouncedSetSearch]);

  // When the debounced searchTerm changes, reset pagination to page 1
  // and refetch. Skip on first mount (initialDataLoadDone handles that).
  const firstSearchRun = useRef(true);
  useEffect(() => {
    if (firstSearchRun.current) {
      firstSearchRun.current = false;
      return;
    }
    setCursor(null);
    setCursorHistory([]);
    getCustomerData('next', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Navigate to next page (decrement - lower IDs)
  const paginateNext = () => {
    if (paginationInfo.hasMore) {
      // Use current cursor (lastCursor = lowest ID on page) or null for first page
      getCustomerData('next', cursor);
    }
  };

  // Navigate to previous page (increment - higher IDs)
  const paginatePrev = () => {
    if (paginationInfo.hasPrev) {
      // For increment navigation, use the lastCursor of the current page
      // This queries records with IDs > lastCursor (higher IDs)
      const prevCursor = paginationInfo.lastCursor;
      if (prevCursor) {
        getCustomerData('prev', prevCursor);
      } else {
        // No lastCursor means we're at top somehow, reset
        setCursor(null);
        setCursorHistory([]);
        getCustomerData('next', null);
      }
    }
  };

  // Calculate approximate page number for display
  // With cursor pagination, we can't know exact page, so we estimate based on total count
  const approximatePageNumber = cursorHistory.length + 1;
  const currentPage = approximatePageNumber || 1;
  const totalPages = Math.max(1, Math.ceil(paginationInfo.totalCount / itemsPerPage));

  useEffect(() => {
    getLastScanned();
    checkIfExists();
    checkIfScanned();
    setProgress(0);
    setProgressMessage("");
    setClickedArea("");
    setInitialLoad(false);
    // Resume-on-reload is handled by the scan-status polling query —
    // if the server reports inProgress=true on first fetch, the effect
    // above flips the progress bar on without needing a button click.
  }, []);

  useEffect(() => { // after findDuplicates finishes
    if (!scanComplete) return;
    getLastScanned();
    checkIfExists();
    checkIfScanned();
    // Reset cursor and load first page
    setCursor(null);
    setCursorHistory([]);
    getCustomerData('next', null);
    // Clear the flag so this effect runs once per scan completion, not
    // again every time getCustomerData re-memoizes (it changes identity
    // when cursor/cursorHistory change — which they do inside
    // getCustomerData itself, causing an infinite re-fetch loop).
    setScanComplete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanComplete]);

  useEffect(() => { // Scan in body, rescan in header
    if (clickedArea === "scan" || clickedArea === "rescan") {
      setShowSpinnerMessage(true);
    }
  }, [clickedArea]);

  return (
    <div className="p-4">

      {/* Header */}
      <Header {...{
        duplicateCount,
        accordionStates,
        setAccordionStates,
        customerData,
        findDuplicates,
        loading2,
        itemsPerPage,
        setItemsPerPage,
        currentPage: currentPage,
        updateItemsPerPage,
        scanComplete,
        yesScanned,
        lastScanned,
        searchInput,
        setSearchInput,
      }} />

      {/* Header Row */}
      <div className="grid grid-cols-12 gap-4 text-[#000000B3] text-sm font-bold mt-6 mb-2">
        <div className="col-span-1 pl-2">% {tr("Match")}</div>
        <div className="col-span-2">{tr("Name")}</div>
        <div className="col-span-3">{tr("Email")}</div>
        <div className="col-span-3">{tr("Address")}</div>
        <div className="col-span-2">{tr("Phone")} #</div>
        <div className="col-span-1">{tr("Tools")}</div>
      </div>
      <div className="border-t border-primary "/>

      {/* Content */}
      {initialLoad || loading ? (
        <div className="text-center mt-8">
          <Spinner size={32} />
        </div>
      ) : (!yesScanned || clickedArea === "scan" || clickedArea === "rescan")  ? (
        <ProgressBar 
          {...{
            showSpinnerMessage,
            loading2,
            findDuplicates,
            progress,
            progressMessage,
            clickedArea,
            yesScanned
          }}
        />
      ) : yesScanned && duplicatesExist ? (
        <Customers 
        setCustomerData={setCustomerData} {...{
          customerData,
          toggleAccordion,
          accordionStates,
          setDuplicateCount,
          loading
        }}        
      />

      ) : (
        ""
      )}

      {/* Pagination */}
      {yesScanned && duplicatesExist && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          paginateNext={paginateNext}
          paginatePrev={paginatePrev}
          hasMore={paginationInfo.hasMore}
          hasPrev={paginationInfo.hasPrev}
        />
      )}

    </div>
  );
}

export default DuplicateCustomers;